/**
 * MenuConfigPage — Navigation & Layout Configuration
 *
 * Allows admins to configure the navigation system without touching code:
 *   - navMode: vertical (sidebar) | horizontal (top-nav)
 *   - mobileNavStyle: tabs | drawer
 *   - Category order: drag-free up/down reorder
 *   - Category visibility: show/hide per category
 *   - Icon override: change icon per category
 *
 * Saves to: platform_settings key `nav:tenant:{tenantId}:config`
 * Takes effect: after config cache expiry (~30s) or page reload
 *
 * Route: /admin/menu-config  (admin/manager only)
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useLayoutConfigContext,
  DEFAULT_CATEGORY_ORDER,
  CATEGORY_LABELS,
  DEFAULT_CATEGORY_ICONS,
  CATEGORY_PERMISSIONS,
  MODULE_NAV_ITEMS,
  MODULE_DEFAULTS,
  type NavigationCategory,
  type NavigationPolicyConfig,
  type SurfaceNavigationConfig,
} from '@/lib/use-layout-config';
import {
  AUTO_TEMPLATE_VALUE,
  buildTenantNavConfigPayload,
} from '@/lib/menu-config-persistence';
import type { WmsTemplateCode } from '@/lib/wms-template-config';
import type {
  WarehouseModernizationStage,
  WarehouseNavigationMode,
  WarehouseNavigationPolicyConfig,
} from '@/lib/warehouse-navigation-governance';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Settings2,
  Loader2,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ─────────────────────────────────────────────────────────────────

interface CategoryRow {
  id: string;
  visible: boolean;
  icon: string;
  requiredPermission: string;
  labelOverride?: string;
  order: number;
}

interface NavConfig {
  navMode: 'vertical' | 'horizontal';
  mobileNavStyle: 'tabs' | 'drawer';
  categoryOrder: string[];
  categoryIcons?: Record<string, string>;
  navigationPolicy?: NavigationPolicyConfig;
  warehouseProfileTemplateCode?: WmsTemplateCode;
  warehouseNavigationPolicy?: WarehouseNavigationPolicyConfig;
  navigation?: CategoryRow[] | NavigationCategory[];
  pwaNavigation?: SurfaceNavigationConfig;
  extensionNavigation?: SurfaceNavigationConfig;
}

type PersistedNavConfig = Partial<NavConfig> & Record<string, unknown>;

interface NavConfigRecord {
  key: string;
  value: NavConfig;
  updated_at?: string;
}

interface CcAdminProfileConfigResponse {
  settingKey: string;
  source: 'database' | 'bundled-failover';
  hasStoredOverride: boolean;
  updatedAt?: string | null;
  profile: Record<string, unknown>;
}

const DEFAULT_WAREHOUSE_MODE: WarehouseNavigationMode = 'compat';
const DEFAULT_WAREHOUSE_STAGE: WarehouseModernizationStage = 'pilot';
const DEFAULT_PRESERVE_LEGACY = true;

const WAREHOUSE_TEMPLATE_OPTIONS: Array<{ value: typeof AUTO_TEMPLATE_VALUE | WmsTemplateCode; label: string; description: string }> = [
  { value: AUTO_TEMPLATE_VALUE, label: 'Auto / No Override', description: 'Keep current tenant runtime resolution.' },
  { value: 'general-3pl', label: 'WM Generic Warehouse', description: '3PL / shared warehouse operations.' },
  { value: 'manufacturing-stores', label: 'WM Manufacturing', description: 'Plant store / line-side / material issue flows.' },
  { value: 'yard-control', label: 'WM Yard Control', description: 'Gate, staging, slot, yard move operations.' },
  { value: 'distribution-hub', label: 'WM Distribution Hub', description: 'Multi-customer distribution hub flows.' },
  { value: 'ecommerce-fulfillment', label: 'WM E-Commerce Fulfillment', description: 'Wave, packing, parcel, return operations.' },
  { value: 'hybrid-flex', label: 'WM Hybrid Warehouse', description: 'Mixed profile fallback while template is still evolving.' },
];

function isCategoryRow(value: unknown): value is CategoryRow {
  const record = value as unknown as Record<string, unknown>;
  return Boolean(value)
    && typeof value === 'object'
    && typeof record.id === 'string'
    && !Array.isArray(record.items);
}

function legacyCategoryRows(value: NavConfig['navigation']): CategoryRow[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isCategoryRow).map((row, index) => ({
    id: row.id,
    visible: typeof row.visible === 'boolean' ? row.visible : true,
    icon: typeof row.icon === 'string' && row.icon.trim().length > 0
      ? row.icon
      : DEFAULT_CATEGORY_ICONS[row.id] ?? 'briefcase',
    requiredPermission: typeof row.requiredPermission === 'string'
      ? row.requiredPermission
      : CATEGORY_PERMISSIONS[row.id] ?? DEFAULT_PERMS[row.id] ?? '',
    labelOverride: row.labelOverride,
    order: typeof row.order === 'number' ? row.order : index,
  }));
}

function toPersistedNavConfig(value: NavConfig | undefined): PersistedNavConfig {
  return (value ?? {}) as unknown as PersistedNavConfig;
}

// ─── Default permission map (mirrors CATEGORY_PERMISSIONS) ──────────────────
const DEFAULT_PERMS: Record<string, string> = {
  trucking:  'job:read',
  freight:   'shipment:read',
  warehouse: 'warehouse:read',
  accounts:  'ledger:read',
  parties:   'customer:read',
  messaging: 'conversation:read',
  agent:     'agent:read',
  config:    'config:read',
};

// ─── Component ───────────────────────────────────────────────────────────────

const ALLOWED_ROLES = new Set(['platform_admin', 'admin', 'local_admin', 'manager']);

export default function MenuConfigPage() {
  const { config } = useLayoutConfigContext();
  const { user } = useAuth();

  // ── Role guard ─────────────────────────────────────────────────────────────
  if (user && !ALLOWED_ROLES.has(user.role ?? '')) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
        <AlertTriangle className="h-10 w-10 text-amber-500" />
        <h2 className="text-lg font-semibold">Access Denied</h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          Only admin or manager accounts can access the navigation configuration.
        </p>
      </div>
    );
  }

  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [navMode, setNavMode] = useState<'vertical' | 'horizontal'>('vertical');
  const [mobileNavStyle, setMobileNavStyle] = useState<'tabs' | 'drawer'>('tabs');
  const [pwaEnabled, setPwaEnabled] = useState(false);
  const [pwaHideExtraActions, setPwaHideExtraActions] = useState(false);
  const [pwaNavigationJson, setPwaNavigationJson] = useState('');
  const [extensionEnabled, setExtensionEnabled] = useState(false);
  const [extensionHideExtraActions, setExtensionHideExtraActions] = useState(false);
  const [extensionNavigationJson, setExtensionNavigationJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingOverride, setLoadingOverride] = useState(true);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [navLocked, setNavLocked] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [savedNavigationPolicy, setSavedNavigationPolicy] = useState<NavigationPolicyConfig>({});
  const [loadedConfigValue, setLoadedConfigValue] = useState<PersistedNavConfig>({});
  const [warehouseTemplateCode, setWarehouseTemplateCode] = useState<typeof AUTO_TEMPLATE_VALUE | WmsTemplateCode>(AUTO_TEMPLATE_VALUE);
  const [warehouseNavMode, setWarehouseNavMode] = useState<WarehouseNavigationMode>(DEFAULT_WAREHOUSE_MODE);
  const [warehouseModernizationStage, setWarehouseModernizationStage] = useState<WarehouseModernizationStage>(DEFAULT_WAREHOUSE_STAGE);
  const [preserveLegacyWarehouseModules, setPreserveLegacyWarehouseModules] = useState(DEFAULT_PRESERVE_LEGACY);
  const [ccProfileJson, setCcProfileJson] = useState('');
  const [ccProfileKey, setCcProfileKey] = useState('sdk:cc-admin:profile');
  const [ccProfileSource, setCcProfileSource] = useState<'database' | 'bundled-failover'>('bundled-failover');
  const [ccProfileHasStoredOverride, setCcProfileHasStoredOverride] = useState(false);
  const [ccProfileUpdatedAt, setCcProfileUpdatedAt] = useState<string | null>(null);
  const [loadingCcProfile, setLoadingCcProfile] = useState(false);
  const [savingCcProfile, setSavingCcProfile] = useState(false);
  const [searchParams] = useSearchParams();
  const [targetTenantId, setTargetTenantId] = useState<string>(searchParams.get('tenantId') || user?.tenantId || '');
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);

  // Fetch tenants if platform_admin
  useEffect(() => {
    if (user?.role === 'platform_admin') {
      api.get<{ id: string; name: string }[]>('/platform/tenant-scope-list')
        .then((res) => {
          if (Array.isArray(res)) {
            setTenants(res);
            if (!targetTenantId && res.length > 0) {
              setTargetTenantId(res[0]?.id ?? '');
            }
          }
        })
        .catch(console.error);
    }
  }, [user?.role, targetTenantId]);

  const loadCcAdminProfile = useCallback(async () => {
    if (user?.role !== 'platform_admin') return;
    setLoadingCcProfile(true);
    try {
      const response = await api.get<CcAdminProfileConfigResponse>('/platform/cc-admin/profile/config');
      setCcProfileKey(response.settingKey);
      setCcProfileSource(response.source);
      setCcProfileHasStoredOverride(response.hasStoredOverride);
      setCcProfileUpdatedAt(response.updatedAt ?? null);
      setCcProfileJson(JSON.stringify(response.profile, null, 2));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load CC admin profile';
      toast.error(msg);
    } finally {
      setLoadingCcProfile(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadCcAdminProfile();
  }, [loadCcAdminProfile]);

  // Build rows from current DSL config or full platform defaults
  const buildRows = useCallback((overrides?: Partial<NavConfig>) => {
    let baseRows: CategoryRow[] = [];

    // For platform_admin, we expose ALL possible categories for configuration
    if (user?.role === 'platform_admin') {
      baseRows = DEFAULT_CATEGORY_ORDER.map((id, i) => ({
        id,
        visible: true,
        icon: DEFAULT_CATEGORY_ICONS[id] ?? 'briefcase',
        requiredPermission: CATEGORY_PERMISSIONS[id] ?? '',
        order: i,
      }));
    } else {
      // Normal admins only configure what their app has computed
      if (!config?.navigation) return;
      baseRows = config.navigation.map((cat, i) => ({
        id: cat.id,
        visible: true,
        icon: cat.icon ?? cat.id,
        requiredPermission: cat.requiredPermission ?? CATEGORY_PERMISSIONS[cat.id] ?? DEFAULT_PERMS[cat.id] ?? '',
        order: i,
      }));
    }

    const legacyRows = legacyCategoryRows(overrides?.navigation);
    const hiddenCategoryIds = new Set((overrides?.navigationPolicy?.hiddenCategoryIds ?? []).map((id) => id.trim().toLowerCase()));
    const savedCategoryIcons = overrides?.categoryIcons ?? {};

    if (!legacyRows.length && !overrides?.categoryOrder?.length && Object.keys(savedCategoryIcons).length === 0 && hiddenCategoryIds.size === 0) {
      setRows(baseRows);
      return;
    }

    const savedMap = new Map(legacyRows.map((r) => [r.id, r]));
    const mergedRows = (overrides?.categoryOrder?.length
      ? overrides.categoryOrder.map((id, i) => {
          const saved = savedMap.get(id);
          const base = baseRows.find((r) => r.id === id) ?? { id, visible: true, icon: DEFAULT_CATEGORY_ICONS[id] ?? id, requiredPermission: CATEGORY_PERMISSIONS[id] ?? DEFAULT_PERMS[id] ?? '', order: i };
          return {
            ...base,
            ...(saved ?? {}),
            icon: savedCategoryIcons[id] ?? saved?.icon ?? base.icon,
            visible: !hiddenCategoryIds.has(id.trim().toLowerCase()) && (saved?.visible ?? true),
            order: i,
          };
        })
      : baseRows.map((r) => {
          const saved = savedMap.get(r.id);
          return {
            ...(saved ? { ...r, ...saved } : r),
            icon: savedCategoryIcons[r.id] ?? saved?.icon ?? r.icon,
            visible: !hiddenCategoryIds.has(r.id.trim().toLowerCase()) && (saved?.visible ?? true),
          };
        }))
      .map((row, index) => ({ ...row, order: index }));

    setRows(mergedRows);
  }, [config, user?.role]);

  // Load existing saved overrides from domain-configs
  useEffect(() => {
    if (!targetTenantId) return;

    const key = `nav:tenant:${targetTenantId}:config`;
    const lockKey = `nav:lock:${targetTenantId}`;
    setLoadingOverride(true);

    // Check lock status
    api.get<{ locked?: boolean; value?: { locked?: boolean } }>(`/domain-configs/${encodeURIComponent(lockKey)}`)
      .then((res) => {
        const val = (typeof res === 'object' && res !== null && 'value' in res)
          ? res.value
          : res as { locked?: boolean };
        setNavLocked(Boolean(val?.locked));
      })
      .catch(() => setNavLocked(false));

    api.get<NavConfig | NavConfigRecord>(`/domain-configs/${encodeURIComponent(key)}`)
      .then((savedRaw) => {
        const saved = (typeof savedRaw === 'object' && savedRaw !== null && 'value' in savedRaw)
          ? (savedRaw as NavConfigRecord).value
          : savedRaw as unknown as NavConfig;
        setLoadedConfigValue(toPersistedNavConfig(saved));
        setNavMode(saved.navMode ?? 'vertical');
        setMobileNavStyle(saved.mobileNavStyle ?? 'tabs');
        setSavedNavigationPolicy(saved.navigationPolicy ?? {});
        setWarehouseTemplateCode((saved.warehouseProfileTemplateCode as WmsTemplateCode | undefined) ?? AUTO_TEMPLATE_VALUE);
        setWarehouseNavMode(saved.warehouseNavigationPolicy?.mode ?? DEFAULT_WAREHOUSE_MODE);
        setWarehouseModernizationStage(saved.warehouseNavigationPolicy?.wmModernizationStage ?? DEFAULT_WAREHOUSE_STAGE);
        setPreserveLegacyWarehouseModules(saved.warehouseNavigationPolicy?.preserveLegacyModulesUntilReady ?? DEFAULT_PRESERVE_LEGACY);
        setPwaEnabled(Boolean(saved.pwaNavigation?.navigation?.length));
        setPwaHideExtraActions(Boolean(saved.pwaNavigation?.hideExtraActions));
        setPwaNavigationJson(
          saved.pwaNavigation?.navigation?.length
            ? JSON.stringify(saved.pwaNavigation.navigation, null, 2)
            : '',
        );
        setExtensionEnabled(Boolean(saved.extensionNavigation?.navigation?.length));
        setExtensionHideExtraActions(Boolean(saved.extensionNavigation?.hideExtraActions));
        setExtensionNavigationJson(
          saved.extensionNavigation?.navigation?.length
            ? JSON.stringify(saved.extensionNavigation.navigation, null, 2)
            : '',
        );
        buildRows(saved);
      })
      .catch(() => {
        // No saved config yet — use DSL defaults
        setLoadedConfigValue({});
        setSavedNavigationPolicy({});
        setWarehouseTemplateCode(AUTO_TEMPLATE_VALUE);
        setWarehouseNavMode(DEFAULT_WAREHOUSE_MODE);
        setWarehouseModernizationStage(DEFAULT_WAREHOUSE_STAGE);
        setPreserveLegacyWarehouseModules(DEFAULT_PRESERVE_LEGACY);
        buildRows();
      })
      .finally(() => setLoadingOverride(false));
  }, [targetTenantId, buildRows]);

  // ── Reorder ──────────────────────────────────────────────────────────────
  const moveRow = (idx: number, direction: 'up' | 'down') => {
    setRows((prev) => {
      const next = [...prev];
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[idx]!, next[target]!] = [next[target]!, next[idx]!];
      return next.map((r, i) => ({ ...r, order: i }));
    });
  };

  // ── Toggle visibility ─────────────────────────────────────────────────────
  const toggleVisible = (id: string) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, visible: !r.visible } : r));
  };

  // ── Edit field ────────────────────────────────────────────────────────────
  const editField = (id: string, field: keyof CategoryRow, value: string) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!targetTenantId) return;
    setSaving(true);
    setError(null);
    try {
      let pwaNavigation: SurfaceNavigationConfig | undefined;
      let extensionNavigation: SurfaceNavigationConfig | undefined;
      if (pwaEnabled) {
        const parsed = JSON.parse(pwaNavigationJson || '[]') as NavigationCategory[] | SurfaceNavigationConfig;
        if (Array.isArray(parsed)) {
          pwaNavigation = {
            navigation: parsed,
            hideExtraActions: pwaHideExtraActions,
          };
        } else {
          if (!Array.isArray(parsed.navigation)) {
            throw new Error('PWA Navigation JSON must be a category array or an object with navigation[]');
          }
          pwaNavigation = {
            navigation: parsed.navigation,
            hideExtraActions: parsed.hideExtraActions ?? pwaHideExtraActions,
          };
        }
      }
      if (extensionEnabled) {
        const parsed = JSON.parse(extensionNavigationJson || '[]') as NavigationCategory[] | SurfaceNavigationConfig;
        if (Array.isArray(parsed)) {
          extensionNavigation = {
            navigation: parsed,
            hideExtraActions: extensionHideExtraActions,
          };
        } else {
          if (!Array.isArray(parsed.navigation)) {
            throw new Error('Extension Navigation JSON must be a category array or an object with navigation[]');
          }
          extensionNavigation = {
            navigation: parsed.navigation,
            hideExtraActions: parsed.hideExtraActions ?? extensionHideExtraActions,
          };
        }
      }

      const payload = buildTenantNavConfigPayload({
        loadedConfigValue,
        rows,
        navMode,
        mobileNavStyle,
        savedNavigationPolicy,
        pwaNavigation,
        extensionNavigation,
        warehouseTemplateCode,
        warehouseNavMode,
        warehouseModernizationStage,
        preserveLegacyWarehouseModules,
      });
      const key = `nav:tenant:${targetTenantId}:config`;
      await api.put(`/domain-configs/${encodeURIComponent(key)}`, payload);
      setLoadedConfigValue(payload);
      setSavedNavigationPolicy(payload.navigationPolicy ?? {});
      setLastSaved(new Date().toLocaleTimeString());
      toast.success('Navigation config saved. Changes take effect after ~30s or on next page load.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      if (msg.includes('NAV_LOCKED') || msg.toLowerCase().includes('locked by platform admin')) {
        setNavLocked(true);
        toast.error('Navigation config is locked by platform admin and cannot be modified.');
      } else {
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Reset to DSL defaults ─────────────────────────────────────────────────
  const handleReset = () => {
    setNavMode('vertical');
    setMobileNavStyle('tabs');
    setPwaEnabled(false);
    setPwaHideExtraActions(false);
    setPwaNavigationJson('');
    setExtensionEnabled(false);
    setExtensionHideExtraActions(false);
    setExtensionNavigationJson('');
    setWarehouseTemplateCode(AUTO_TEMPLATE_VALUE);
    setWarehouseNavMode(DEFAULT_WAREHOUSE_MODE);
    setWarehouseModernizationStage(DEFAULT_WAREHOUSE_STAGE);
    setPreserveLegacyWarehouseModules(DEFAULT_PRESERVE_LEGACY);
    buildRows(); // no overrides
    toast.info('Reset to DSL defaults. Save to apply.');
  };

  const toggleLock = async () => {
    if (!targetTenantId) return;
    try {
      await api.put(`/domain-configs/nav/lock/${targetTenantId}`, { locked: !navLocked });
      setNavLocked(!navLocked);
      toast.success(`Navigation config ${!navLocked ? 'locked' : 'unlocked'} for tenant.`);
    } catch (e) {
      toast.error('Failed to change lock status');
    }
  };

  const handleSaveCcProfile = async () => {
    if (user?.role !== 'platform_admin') return;
    setSavingCcProfile(true);
    try {
      const profile = JSON.parse(ccProfileJson || '{}') as Record<string, unknown>;
      await api.put('/platform/cc-admin/profile/config', { profile });
      toast.success('CC admin profile saved. Reload the CC shell to pick up the new sidebar/profile.');
      await loadCcAdminProfile();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save CC admin profile';
      toast.error(msg);
    } finally {
      setSavingCcProfile(false);
    }
  };

  const isLoading = loadingOverride;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Platform Admin Tenant Selector */}
      {user?.role === 'platform_admin' && (
        <Card className="border-indigo-100 bg-indigo-50/50 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <Settings2 className="h-5 w-5 text-indigo-600" />
            <div className="grid gap-1">
              <p className="text-sm font-medium text-indigo-900">Platform Admin Mode</p>
              <p className="text-xs text-indigo-700">Select a tenant to edit their navigation config.</p>
            </div>
            <div className="ml-auto flex flex-1 max-w-[400px] items-center gap-3 justify-end">
              <Select value={targetTenantId} onValueChange={setTargetTenantId}>
                <SelectTrigger className="bg-white min-w-[200px] flex-1">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.id.slice(0,8)}...)</SelectItem>
                  ))}
                  {user?.tenantId && !tenants.find(t => t.id === user.tenantId) && (
                    <SelectItem value={user.tenantId}>Current Tenant</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button
                variant={navLocked ? 'destructive' : 'outline'}
                size="sm"
                onClick={toggleLock}
                title={navLocked ? 'Unlock tenant navigation config' : 'Lock tenant navigation config'}
              >
                {navLocked ? 'Unlock' : 'Lock'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {user?.role === 'platform_admin' && (
        <Card className="border-sky-100 bg-sky-50/50 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings2 className="h-4 w-4 text-sky-700" />
              CC Admin SDK Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 text-sm text-sky-950">
              <p>
                Tenant 菜单存放在 <code className="rounded bg-white px-1 py-0.5">{`nav:tenant:${targetTenantId || '{tenantId}'}:config`}</code>，
                CC admin 左侧栏存放在 <code className="rounded bg-white px-1 py-0.5">{ccProfileKey}</code>。
              </p>
              <p className="text-xs text-sky-800">
                这两套配置完全分开。修改下面的 JSON 会影响 3002 / CC shell；不会改 tenant runtime 菜单。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant={ccProfileSource === 'database' ? 'default' : 'secondary'}>
                source: {ccProfileSource}
              </Badge>
              <Badge variant={ccProfileHasStoredOverride ? 'default' : 'outline'}>
                {ccProfileHasStoredOverride ? 'stored override' : 'bundled fallback'}
              </Badge>
              {ccProfileUpdatedAt && (
                <Badge variant="outline">
                  updated: {new Date(ccProfileUpdatedAt).toLocaleString()}
                </Badge>
              )}
            </div>
            <Textarea
              value={ccProfileJson}
              onChange={(event) => setCcProfileJson(event.target.value)}
              className="min-h-[260px] font-mono text-xs"
              placeholder={loadingCcProfile ? 'Loading CC admin profile…' : 'CC admin profile JSON'}
              disabled={loadingCcProfile || savingCcProfile}
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={loadCcAdminProfile} disabled={loadingCcProfile || savingCcProfile}>
                {loadingCcProfile ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
                Reload CC Profile
              </Button>
              <Button size="sm" onClick={handleSaveCcProfile} disabled={loadingCcProfile || savingCcProfile}>
                {savingCcProfile ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
                Save CC Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Nav-locked banner */}
      {navLocked && user?.role !== 'platform_admin' && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Navigation config is <strong>locked by platform admin</strong>. You can view but not save changes.</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6" />
            Navigation Config
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure menu layout, category order, visibility, and permissions.
            Changes saved to <code className="text-xs bg-muted px-1 py-0.5 rounded">platform_settings</code> and take effect automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <RefreshCw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || isLoading || (navLocked && user?.role !== 'platform_admin')}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {lastSaved && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="h-4 w-4" /> Saved at {lastSaved}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Layout Mode */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Layout Mode</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="navMode">Web Nav Mode</Label>
            <Select value={navMode} onValueChange={(v) => setNavMode(v as 'vertical' | 'horizontal')}>
              <SelectTrigger id="navMode" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">Vertical (Sidebar)</SelectItem>
                <SelectItem value="horizontal">Horizontal (Top Nav)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Controls web desktop navigation style.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mobileStyle">Mobile Nav Style</Label>
            <Select value={mobileNavStyle} onValueChange={(v) => setMobileNavStyle(v as 'tabs' | 'drawer')}>
              <SelectTrigger id="mobileStyle" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tabs">Bottom Tabs</SelectItem>
                <SelectItem value="drawer">Drawer</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Controls go-mobile navigation pattern.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">WM / WG / WP Transition Policy</CardTitle>
          <p className="text-sm text-muted-foreground">
            Controls the warehouse migration path. `CF` remains a separate module and is not governed here.
          </p>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="warehouseTemplateCode">WM Template Context</Label>
            <Select
              value={warehouseTemplateCode}
              onValueChange={(value) => setWarehouseTemplateCode(value as typeof AUTO_TEMPLATE_VALUE | WmsTemplateCode)}
            >
              <SelectTrigger id="warehouseTemplateCode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WAREHOUSE_TEMPLATE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {WAREHOUSE_TEMPLATE_OPTIONS.find((option) => option.value === warehouseTemplateCode)?.description}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="warehouseNavMode">Transition Mode</Label>
            <Select
              value={warehouseNavMode}
              onValueChange={(value) => setWarehouseNavMode(value as WarehouseNavigationMode)}
            >
              <SelectTrigger id="warehouseNavMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="compat">compat: keep WG / WP / WM</SelectItem>
                <SelectItem value="wm-primary">wm-primary: WM first, WG/WP legacy</SelectItem>
                <SelectItem value="wm-only">wm-only: WM only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Use `compat` until WM is stable. `wm-only` should only be used after production readiness is proven.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="warehouseModernizationStage">Modernization Stage</Label>
            <Select
              value={warehouseModernizationStage}
              onValueChange={(value) => setWarehouseModernizationStage(value as WarehouseModernizationStage)}
            >
              <SelectTrigger id="warehouseModernizationStage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="incubating">incubating</SelectItem>
                <SelectItem value="pilot">pilot</SelectItem>
                <SelectItem value="ready">ready</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Runtime will force fallback to `compat` while the stage is not ready and legacy preservation is enabled.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="preserveLegacyWarehouseModules">Preserve WG / WP until WM is ready</Label>
              <p className="text-xs text-muted-foreground">
                Recommended during rollout. This blocks accidental cutover before WM reaches `ready`.
              </p>
            </div>
            <Switch
              id="preserveLegacyWarehouseModules"
              checked={preserveLegacyWarehouseModules}
              onCheckedChange={setPreserveLegacyWarehouseModules}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">PWA Surface Navigation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="pwaEnabled">Use custom PWA navigation</Label>
              <p className="text-xs text-muted-foreground">
                When enabled, go-pwa uses this menu instead of the full tenant navigation.
              </p>
            </div>
            <Switch id="pwaEnabled" checked={pwaEnabled} onCheckedChange={setPwaEnabled} />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="pwaHideExtraActions">Hide extra chat button</Label>
              <p className="text-xs text-muted-foreground">
                Useful when AI Chat is already included as a PWA menu category.
              </p>
            </div>
            <Switch
              id="pwaHideExtraActions"
              checked={pwaHideExtraActions}
              onCheckedChange={setPwaHideExtraActions}
              disabled={!pwaEnabled}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pwaNavigationJson">PWA Navigation JSON</Label>
            <Textarea
              id="pwaNavigationJson"
              value={pwaNavigationJson}
              onChange={(e) => setPwaNavigationJson(e.target.value)}
              disabled={!pwaEnabled}
              className="min-h-[260px] font-mono text-xs"
              placeholder={`[\n  {\n    "id": "messaging",\n    "label": "Messaging",\n    "icon": "mail",\n    "order": 1,\n    "items": []\n  }\n]`}
            />
            <p className="text-xs text-muted-foreground">
              Accepts either a category array or an object with <code>navigation</code> and <code>hideExtraActions</code>.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Extension Surface Navigation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="extensionEnabled">Use custom extension navigation</Label>
              <p className="text-xs text-muted-foreground">
                When enabled, chrome extension uses this menu instead of the full tenant navigation.
              </p>
            </div>
            <Switch id="extensionEnabled" checked={extensionEnabled} onCheckedChange={setExtensionEnabled} />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="extensionHideExtraActions">Hide extra chat button</Label>
              <p className="text-xs text-muted-foreground">
                Useful when AI Chat is already included as an extension menu category.
              </p>
            </div>
            <Switch
              id="extensionHideExtraActions"
              checked={extensionHideExtraActions}
              onCheckedChange={setExtensionHideExtraActions}
              disabled={!extensionEnabled}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="extensionNavigationJson">Extension Navigation JSON</Label>
            <Textarea
              id="extensionNavigationJson"
              value={extensionNavigationJson}
              onChange={(e) => setExtensionNavigationJson(e.target.value)}
              disabled={!extensionEnabled}
              className="min-h-[260px] font-mono text-xs"
              placeholder={`[\n  {\n    "id": "automation",\n    "label": "Automation",\n    "icon": "zap",\n    "order": 1,\n    "items": []\n  }\n]`}
            />
            <p className="text-xs text-muted-foreground">
              Accepts either a category array or an object with <code>navigation</code> and <code>hideExtraActions</code>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Category Order & Config */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Domain Categories</CardTitle>
          <p className="text-sm text-muted-foreground">
            Reorder, toggle visibility, and set permission requirements per category.
            Hidden categories are not shown in the sidebar (users with the permission won't see them).
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading config…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No categories found. Check entity definitions.</p>
          ) : (
            <div className="flex flex-col divide-y">
              {/* Header row */}
              <div className="grid grid-cols-[2fr_2fr_2fr_1fr_auto] gap-3 px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Category</span>
                <span>Icon Key</span>
                <span>Module Gate <span className="normal-case font-normal opacity-60">(requires module ON)</span></span>
                <span>Visible</span>
                <span className="w-16">Order</span>
              </div>
              {rows.map((row, idx) => {
                const catLabel = CATEGORY_LABELS[row.id] ?? row.id;
                const catItems = config?.navigation?.find(c => c.id === row.id)?.items
                  ?? (MODULE_NAV_ITEMS as Record<string, {label:string;route:string}[]>)[row.id]
                  ?? [];
                const isExpanded = expandedRow === row.id;
                return (
                <div key={row.id}>
                <div
                  key={row.id}
                  className={`grid grid-cols-[2fr_2fr_2fr_1fr_auto] gap-3 items-center py-3 px-2 ${!row.visible ? 'opacity-50' : ''}`}
                >
                  {/* Category name */}
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center gap-1.5 text-left focus:outline-none"
                      onClick={() => setExpandedRow(isExpanded ? null : row.id)}
                      title={`${isExpanded ? 'Hide' : 'Show'} items in ${catLabel}`}
                    >
                      <ChevronRight className={`h-3 w-3 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <Badge variant={row.visible ? 'default' : 'secondary'} className="text-xs font-mono">
                        {row.id}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{catLabel}</span>
                    </button>
                  </div>

                  {/* Icon key */}
                  <Input
                    value={row.icon}
                    onChange={(e) => editField(row.id, 'icon', e.target.value)}
                    className="h-7 text-xs font-mono"
                    placeholder="e.g. truck"
                  />

                  {/* Module Gate */}
                  {(() => {
                    const modCode = MODULE_DEFAULTS[row.id];
                    const perm = CATEGORY_PERMISSIONS[row.id] ?? row.requiredPermission;
                    return (
                      <div className="flex flex-col gap-0.5">
                        {modCode ? (
                          <Badge variant="outline" className="w-fit text-xs font-mono font-semibold text-primary border-primary/40">
                            {modCode}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="w-fit text-xs font-mono">
                            always on
                          </Badge>
                        )}
                        {perm && perm !== '*' && (
                          <span className="text-[10px] text-muted-foreground font-mono leading-none">{perm}</span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Visible toggle */}
                  <div className="flex justify-center">
                    <Switch
                      checked={row.visible}
                      onCheckedChange={() => toggleVisible(row.id)}
                    />
                  </div>

                  {/* Reorder buttons */}
                  <div className="flex items-center gap-1 w-16">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveRow(idx, 'up')}
                      disabled={idx === 0}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveRow(idx, 'down')}
                      disabled={idx === rows.length - 1}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {/* Expanded items preview */}
                {isExpanded && catItems.length > 0 && (
                  <div className="mx-2 mb-3 rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 px-3 py-2">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Nav items in <strong>{catLabel}</strong> ({catItems.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {catItems.map((item: {label:string;route:string;id?:string}) => (
                        <span key={item.id ?? item.route} className="inline-flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{item.label}</span>
                          <span className="opacity-50">{item.route}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {isExpanded && catItems.length === 0 && (
                  <div className="mx-2 mb-3 rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 px-3 py-2">
                    <p className="text-xs text-muted-foreground">No fixed items — populated from entity registry at runtime.</p>
                  </div>
                )}
                </div>
              );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />
      <p className="text-xs text-muted-foreground">
        Config saved to <code className="bg-muted px-1 rounded">{`platform_settings[nav:tenant:${targetTenantId || '{tenantId}'}:config]`}</code>.
        Category show/hide is persisted as <code className="bg-muted px-1 rounded">navigationPolicy.hiddenCategoryIds</code>.
        WM migration is persisted as <code className="bg-muted px-1 rounded">warehouseNavigationPolicy</code> plus <code className="bg-muted px-1 rounded">warehouseProfileTemplateCode</code>.
        Takes effect after ~30s cache expiry. Entities and their assignments are defined in{' '}
        <code className="bg-muted px-1 rounded">entity-definitions.ts</code> and cannot be changed here.
      </p>
    </div>
  );
}
